import { createTheme } from '@mui/material/styles';

export type ThemeMode = 'light' | 'dark';

/**
 * Create Material-UI theme based on mode (light/dark)
 * @param mode - Theme mode ('light' or 'dark')
 * @returns Material-UI theme object
 */
export const createAppTheme = (mode: ThemeMode) => {
  const theme = createTheme({
    palette: {
      mode,
    },
    typography: {
      fontFamily: '"Arial", "Helvetica", sans-serif',
    },
    shape: {
      borderRadius: 12,
    },
  });

  return createTheme({
    ...theme,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            '&:focus-visible': {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '50%',
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&:focus-visible': {
              outline: `3px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '3px',
                  borderColor: theme.palette.primary.main,
                },
              },
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: 'background-color 0.3s ease, color 0.3s ease',
          },
          // Screen reader only content
          '.sr-only': {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          },
          // Skip link for keyboard navigation
          '.skip-link': {
            position: 'absolute',
            top: '-40px',
            left: '6px',
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            padding: '8px',
            textDecoration: 'none',
            borderRadius: '4px',
            zIndex: 1000,
            '&:focus': {
              top: '6px',
            },
          },
        },
      },
    },
  });
};

// Default light theme for backward compatibility
const theme = createAppTheme('light');

export default theme;
